// DashAIAuthAdapter — Bridges DashONE's Redux auth state to DashmoteAI's useAuth() context.
//
// DashmoteAI components call useAuth() expecting { user, firestoreUser, loading, signOut, ... }.
// This adapter reads from DashONE's Redux store (authSlice) and constructs that shape.
//
// Enrichment: When the user navigates to /ai/* routes, we call the dashaiUser
// Firebase Cloud Function to get DashmoteAI-specific fields (geoscope, pet_formula,
// product_scopes, accessible_countries) that DashONE's FastAPI doesn't provide.

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { User } from 'firebase/auth';
import { auth } from '@/utils/firebase';
import { performLogout } from '@/utils/performLogout';
import { firebaseFunctionHeaders } from '@dashai/lib/firebaseFunctionHeaders';
import type { FirestoreUser } from './AuthContext';

// Re-export types so consumers don't need to change imports
export type { FirestoreUser, ClientPermission, GeoscopeRegion } from './AuthContext';

interface DashAIAuthContextType {
  user: User | null;
  firestoreUser: FirestoreUser | null;
  loading: boolean;
  enrichLoading: boolean;
  // True when the last completed enrichment fetch came back empty. A 404
  // resolves to a minimal user rather than null, so this means the request
  // itself failed. Callers that depend on enriched-only fields (geoscope,
  // accessible_countries, pet_formula) need to tell that apart from a user who
  // legitimately has none, because the two need different words to the user.
  enrichFailed: boolean;
  signInWithEmail: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUpWithEmail: (email: string, password: string, name: string) => Promise<{ success: boolean; error?: string }>;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshFirestoreUser: () => Promise<void>;
}

export const DashAIAuthContext = createContext<DashAIAuthContextType | undefined>(undefined);

// Fetch enriched user data from the dashaiUser Firebase Cloud Function.
// Returns the full FirestoreUser with geoscope, pet_formula, accessible_countries.
//
// `clientId` names the workspace being viewed. Dashmote staff reach every
// workspace without an `id_client` grant, so for them the function has no
// membership to enrich and only emits that workspace's scope when asked for it
// by name. A member's own workspaces come back either way.
async function fetchEnrichedUser(
  firebaseUser: User,
  clientId?: string,
): Promise<FirestoreUser | null> {
  try {
    const idToken = await firebaseUser.getIdToken();
    const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
    if (!base) {
      console.error('[DashAIAuthAdapter] VITE_FIREBASE_FUNCTIONS_URL not set');
      return null;
    }
    const query = clientId ? `?id_client=${encodeURIComponent(clientId)}` : '';
    const response = await fetch(`${base.replace(/\/+$/, '')}/dashaiUser${query}`, {
      method: 'GET',
      headers: firebaseFunctionHeaders(idToken),
    });

    if (!response.ok) {
      if (response.status === 404) {
        // User not found — return minimal user
        return {
          userId: firebaseUser.uid,
          email: firebaseUser.email || '',
          name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
          application_role: 'viewer',
          workspaces: [],
          default_id_client: '',
          id_client: {},
          createdAt: new Date(),
          updatedAt: new Date(),
        };
      }
      console.error('[DashAIAuthAdapter] Edge function error:', response.status);
      return null;
    }

    const data = await response.json();
    return {
      userId: data.id_user,
      email: data.email,
      name: data.name || firebaseUser.displayName || '',
      application_role: data.application_role || 'viewer',
      workspaces: data.workspaces || [],
      default_id_client: data.default_id_client || '',
      id_client: data.id_client || {},
      createdAt: data.created_at ? new Date(data.created_at) : new Date(),
      updatedAt: data.updated_at ? new Date(data.updated_at) : new Date(),
    };
  } catch (error) {
    console.error('[DashAIAuthAdapter] Error fetching enriched user:', error);
    return null;
  }
}

export function DashAIAuthAdapter({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();

  // Read from DashONE's Redux auth state
  const { reduxUser, userData, selectedWorkspace, isAuthenticated } = useSelector(
    (state: any) => ({
      reduxUser: state.auth.user,
      userData: state.auth.userData,
      selectedWorkspace: state.auth.selectedWorkspace,
      isAuthenticated: state.auth.isAuthenticated,
    }),
    shallowEqual
  );

  const [enrichedUser, setEnrichedUser] = useState<FirestoreUser | null>(null);
  const [enrichLoading, setEnrichLoading] = useState(true);
  const [enrichFailed, setEnrichFailed] = useState(false);

  // Get the actual Firebase User object (Redux stores a plain object, not the Firebase User)
  const firebaseUser = auth.currentUser;

  // Fetch enriched user data when user or workspace changes
  useEffect(() => {
    if (!firebaseUser || !isAuthenticated) {
      setEnrichedUser(null);
      setEnrichFailed(false);
      setEnrichLoading(false);
      return;
    }

    let cancelled = false;
    setEnrichLoading(true);

    fetchEnrichedUser(firebaseUser, selectedWorkspace?.key).then((data) => {
      if (!cancelled) {
        setEnrichedUser(data);
        setEnrichFailed(data === null);
        setEnrichLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [firebaseUser?.uid, selectedWorkspace?.key, isAuthenticated]);

  // Build the FirestoreUser that DashmoteAI components expect.
  // Always use Redux userData as the source of truth for id_client/workspaces (populated at login).
  // Overlay geoscope and pet_formula from enrichedUser when available — these are the only fields
  // that Redux doesn't carry. This avoids a dependency on the edge function for workspace listing
  // and prepares for a future migration to the Python backend.
  const firestoreUser: FirestoreUser | null = useMemo(() => {
    if (!reduxUser || !userData) return null;

    // Build id_client from Redux, overlaying geoscope/pet_formula from enrichedUser per client
    const reduxClientIds = Object.keys(userData.id_client || {});
    const id_client: Record<string, any> = {};
    for (const clientId of reduxClientIds) {
      id_client[clientId] = {
        ...userData.id_client[clientId],
        // Overlay enriched fields if available for this client
        ...(enrichedUser?.id_client?.[clientId]
          ? {
              geoscope: enrichedUser.id_client[clientId].geoscope,
              pet_formula: enrichedUser.id_client[clientId].pet_formula,
              accessible_countries: enrichedUser.id_client[clientId].accessible_countries,
              onboarding_markets: enrichedUser.id_client[clientId].onboarding_markets,
            }
          : {}),
      };
    }

    return {
      userId: reduxUser.uid,
      email: reduxUser.email || '',
      name: reduxUser.displayName || userData.name || '',
      account_type: userData.account_type,
      application_role: userData.application_role || 'viewer',
      workspaces: reduxClientIds,
      default_id_client: userData.default_id_client || '',
      id_client,
      // User-level Dashmote-admin flag (the /admin gate). Prefer Redux userData
      // (login-baked) and fall back to the live enrichedUser response, so an
      // admin whose login predates the flag still resolves without re-login.
      dashmote_admin: Boolean(userData.dashmote_admin ?? enrichedUser?.dashmote_admin),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }, [reduxUser, userData, enrichedUser]);

  const handleSignOut = useCallback(async () => {
    // Hard-navigate (full index.html reload) so DashAI sign-out lands the next
    // session on the latest bundle, not this tab's stale one.
    await performLogout(dispatch);
  }, [dispatch]);

  const refreshFirestoreUser = useCallback(async () => {
    if (firebaseUser) {
      setEnrichLoading(true);
      const data = await fetchEnrichedUser(firebaseUser, selectedWorkspace?.key);
      setEnrichedUser(data);
      setEnrichFailed(data === null);
      setEnrichLoading(false);
    }
  }, [firebaseUser, selectedWorkspace?.key]);

  // Sign-in methods are no-ops in the adapter — DashONE's login page handles auth.
  // These exist only to satisfy the AuthContextType interface.
  const signInWithEmail = useCallback(async () => {
    return { success: false, error: 'Use DashONE login page' };
  }, []);
  const signUpWithEmail = useCallback(async () => {
    return { success: false, error: 'Use DashONE login page' };
  }, []);
  const signInWithGoogle = useCallback(async () => {
    return { success: false, error: 'Use DashONE login page' };
  }, []);

  // Build the User-like object from Redux state (Firebase User shape)
  const user: User | null = firebaseUser;

  // Don't block on enrichment — Redux userData is available immediately after login.
  // enrichedUser only adds geoscope/pet_formula overlays which load in the background.
  const loading = !isAuthenticated || !userData;

  const value: DashAIAuthContextType = {
    user,
    firestoreUser,
    loading,
    enrichLoading,
    enrichFailed,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut: handleSignOut,
    refreshFirestoreUser,
  };

  return (
    <DashAIAuthContext.Provider value={value}>
      {children}
    </DashAIAuthContext.Provider>
  );
}

// This hook is intentionally named useAuth() to match what DashmoteAI components expect.
// When DashmoteAI code imports { useAuth } from its AuthContext, we'll redirect that import.
export function useDashAIAuth() {
  const context = useContext(DashAIAuthContext);
  if (context === undefined) {
    throw new Error('useDashAIAuth must be used within a DashAIAuthAdapter');
  }
  return context;
}
